import { lazy, Suspense, useState, type ReactNode } from "react";
import { Button, Center, Loader, Stack, Text } from "@mantine/core";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { SessionProvider, useSession } from "./lib/session";
import { AppLayout } from "./components/AppLayout";
import { LoginPage } from "./pages/Login";
import { SignupPage, SignupVerifyPage } from "./pages/Signup";
import { HomePage } from "./pages/Home";
import { SearchPage } from "./pages/Search";
import { FoodDetailPage } from "./pages/FoodDetail";
import { BarcodePage } from "./pages/Barcode";
import { PhotoPage } from "./pages/Photo";
import { TextToFoodsPage } from "./pages/TextToFoods";
import { InsightsPage } from "./pages/Insights";
import { DishIngredientsPage } from "./pages/DishIngredients";
import { LinkIngredientsPage } from "./pages/LinkIngredients";
import { AlternativesPage } from "./pages/Alternatives";
import { AdvisorPage } from "./pages/Advisor";
import { MealPlansPage } from "./pages/MealPlans";
import { ShoppingPage } from "./pages/Shopping";
import { ReferencePage } from "./pages/Reference";
import { GiCoachPage } from "./pages/GiCoach";
import { GrocerySwapsPage } from "./pages/GrocerySwaps";
import { TourPage } from "./pages/Tour";
import { NutritionMathPage } from "./pages/NutritionMath";
import { DevelopersPage } from "./pages/Developers";
import { ModelsPage } from "./pages/Models";
import { ApiGuidePage } from "./pages/ApiGuide";
import { StudioPage } from "./pages/Studio";
import { PantryRecipePage } from "./pages/PantryRecipe";
import { RecreateDishPage } from "./pages/RecreateDish";
import { RecipeMakeoverPage } from "./pages/RecipeMakeover";
import { SubstitutesPage } from "./pages/Substitutes";
import { MenuCoachPage } from "./pages/MenuCoach";
import { ConditionCheckPage } from "./pages/ConditionCheck";
import { DailyReviewPage } from "./pages/DailyReview";
import { DynamicToolPage } from "./pages/DynamicTool";
import { RequireTool } from "./components/common";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { usePageViews } from "./lib/track";

const AdminApp = lazy(() => import("./admin/AdminApp"));

function FullPageLoader() {
  return (
    <Center mih="100vh">
      <Loader color="lime" />
    </Center>
  );
}

/** Tool pages: [path, DEMO_TOOLS id, page]. */
const TOOL_PAGES: [string, string, ReactNode][] = [
  ["tour", "tour", <TourPage />],
  ["search", "search", <SearchPage />],
  ["barcode", "barcode", <BarcodePage />],
  ["photo", "photo", <PhotoPage />],
  ["text", "text", <TextToFoodsPage />],
  ["insights", "insights", <InsightsPage />],
  ["dish", "dish", <DishIngredientsPage />],
  ["link", "link", <LinkIngredientsPage />],
  ["alternatives", "alternatives", <AlternativesPage />],
  ["advisor", "advisor", <AdvisorPage />],
  ["gi-coach", "gi_coach", <GiCoachPage />],
  ["meal-plans", "meal_plans", <MealPlansPage />],
  ["grocery-swaps", "grocery_swaps", <GrocerySwapsPage />],
  ["shopping", "shopping", <ShoppingPage />],
  ["reference", "reference", <ReferencePage />],
  ["nutrition-math", "nutrition_math", <NutritionMathPage />],
  ["cook", "pantry_recipe", <PantryRecipePage />],
  ["recreate", "recreate_dish", <RecreateDishPage />],
  ["makeover", "recipe_makeover", <RecipeMakeoverPage />],
  ["substitutes", "substitutes", <SubstitutesPage />],
  ["menu-coach", "menu_coach", <MenuCoachPage />],
  ["condition-check", "condition_check", <ConditionCheckPage />],
  ["daily-review", "daily_review", <DailyReviewPage />],
];

/**
 * Tool pages stay mounted (hidden) once visited, so inputs, results and in-flight runs survive switching
 * tabs. Everything else (home, food detail) renders through the normal outlet.
 */
function KeepAliveTools() {
  const { pathname } = useLocation();
  const active = TOOL_PAGES.find(([path]) => pathname === `/${path}`)?.[0];
  const [visited, setVisited] = useState<string[]>([]);
  if (active && !visited.includes(active)) setVisited([...visited, active]);
  return (
    <>
      {TOOL_PAGES.filter(([path]) => path === active || visited.includes(path)).map(([path, tool, page]) => (
        <div key={path} style={path === active ? undefined : { display: "none" }}>
          <ErrorBoundary inspector>
            <RequireTool tool={tool}>{page}</RequireTool>
          </ErrorBoundary>
        </div>
      ))}
      {!active && (
        <ErrorBoundary key={pathname} inspector>
          <Outlet />
        </ErrorBoundary>
      )}
    </>
  );
}

function DemoApp() {
  const { me, loading, error, refresh } = useSession();
  if (loading) return <FullPageLoader />;
  if (!me) {
    if (error && error.status >= 500) {
      return (
        <Center mih="100vh">
          <Stack align="center">
            <Text>The demo server is unavailable ({error.message}).</Text>
            <Button onClick={() => refresh()}>Retry</Button>
          </Stack>
        </Center>
      );
    }
    return <LoginPage notice={error?.code?.startsWith("account_") ? error.message : undefined} expired={error?.code === "account_expired"} />;
  }
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route element={<KeepAliveTools />}>
          <Route index element={<HomePage />} />
          <Route path="food/:refCode" element={<FoodDetailPage />} />
          <Route path="developers" element={<DevelopersPage />} />
          <Route path="models" element={<ModelsPage />} />
          <Route path="guide" element={<ApiGuidePage />} />
          <Route path="studio" element={<StudioPage />} />
          <Route path="tools/:slug" element={<DynamicToolPage />} />
          {TOOL_PAGES.map(([path]) => (
            <Route key={path} path={path} element={null} />
          ))}
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  usePageViews();
  return (
    <Routes>
      <Route
        path="/admin/*"
        element={
          <Suspense fallback={<FullPageLoader />}>
            <AdminApp />
          </Suspense>
        }
      />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signup/verify" element={<SignupVerifyPage />} />
      <Route
        path="/*"
        element={
          <SessionProvider>
            <DemoApp />
          </SessionProvider>
        }
      />
    </Routes>
  );
}
